import {
  Alert,
  Backdrop,
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Stack,
  TextField,
  Typography,
} from "@mui/material";
import { useMutation, useQuery } from "@tanstack/react-query";
import { useEffect, useMemo, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { formatDisplayDate } from "utils/dateFormat";
import { useParams } from "react-router-dom";
import { toast } from "react-toastify";
import {
  completePublicSigning,
  getPublicDocumentInfo,
  getPublicSigningSession,
  sendPublicDocumentOtp,
  verifyPublicDocumentOtp,
} from "utils/documentService";
import { applyCheckboxChange } from "utils/exclusiveCheckboxes";
import { isFieldVisible, visibleFieldsByPage } from "utils/documentFieldVisibility";
import { signingLinkErrorMessage } from "./signingLinkError";

const sessionStorageKey = (publicToken) => `document-signing-session:${publicToken}`;

/** Fallback gap between verification-code requests, used only if the server omits its own. */
const OTP_RESEND_SECONDS = 30;

const formatCountdown = (seconds) =>
  `${Math.floor(seconds / 60)}:${String(seconds % 60).padStart(2, "0")}`;

/** Display-only overrides for the stored template CSS, which is written for a fixed-width paged
 * PDF. The server renders the signed PDF from the template untouched, so none of this reaches the
 * document itself. The narrow-screen block exists because the footer signature cell is 36% of a
 * fixed-layout table, which on a phone is too little room for a signature to sit in. */
const SIGNING_DISPLAY_STYLE = `
  .global-footer { position: static !important; border-top: 1px solid #ccc; margin-top: 12px; padding-top: 8px; }
  .page-header-repeat { position: static !important; }
  @media (max-width: 600px) {
    .signing-page-sheet { padding: 12px !important; }
    .ka-doc .footer-table .footer-label { width: 34%; }
    .ka-doc .footer-table .footer-sig { width: 46%; }
    .ka-doc .footer-table .footer-sig .sig-line { margin-left: 6px; }
  }
`;

/** The canvas is laid out at whatever width the dialog gives it while its drawing surface stays a
 * fixed 900x300, so a pointer position has to be scaled into that surface or the stroke lands
 * somewhere else entirely. */
const canvasPoint = (canvas, event) => {
  if (!canvas) return null;
  const rect = canvas.getBoundingClientRect();
  if (!rect.width || !rect.height) return null;
  return {
    x: ((event.clientX - rect.left) * canvas.width) / rect.width,
    y: ((event.clientY - rect.top) * canvas.height) / rect.height,
  };
};

export default function DocumentSigningPage() {
  const { publicToken } = useParams();
  const [otpCode, setOtpCode] = useState("");
  const [otpCooldown, setOtpCooldown] = useState(0);
  const [otpSentOnce, setOtpSentOnce] = useState(false);
  const [accessToken, setAccessToken] = useState(() => sessionStorage.getItem(sessionStorageKey(publicToken)) || "");
  const [fieldValues, setFieldValues] = useState({});
  const [assetUrls, setAssetUrls] = useState({});
  const [signatureDialogOpen, setSignatureDialogOpen] = useState(false);
  const [activeSignatureField, setActiveSignatureField] = useState("");
  const [confirmSubmitOpen, setConfirmSubmitOpen] = useState(false);
  const [submitBarPinned, setSubmitBarPinned] = useState(false);
  const submitSentinelRef = useRef(null);
  const canvasRef = useRef(null);
  const isDrawingRef = useRef(false);
  const fieldRefs = useRef({});
  const combRefs = useRef({});
  const htmlContainerRef = useRef(null);
  const [portalNodes, setPortalNodes] = useState({});
  const prefilledNamesRef = useRef(new Set());

  const setCombChar = (field, index, char) => {
    setFieldValues((current) => {
      const chars = (current[field.name] || "").padEnd(field.combCells, " ").split("");
      chars[index] = char || " ";
      return { ...current, [field.name]: chars.join("") };
    });
  };

  const infoQuery = useQuery({
    queryKey: ["public-document-info", publicToken],
    queryFn: () => getPublicDocumentInfo(publicToken),
    retry: false,
    refetchOnWindowFocus: false,
    refetchOnReconnect: false,
  });

  // The cooldown lives on the packet, not in this tab: seeding it from the server means reloading the
  // page (or opening the link in another tab) can't hand the visitor a fresh Send button.
  useEffect(() => {
    if (infoQuery.data?.otpSent) setOtpSentOnce(true);
    const remaining = infoQuery.data?.otpRetryAfterSeconds ?? 0;
    if (remaining > 0) setOtpCooldown(remaining);
  }, [infoQuery.data?.otpSent, infoQuery.data?.otpRetryAfterSeconds]);

  useEffect(() => {
    if (otpCooldown <= 0) return undefined;
    const timer = setInterval(() => setOtpCooldown((seconds) => Math.max(0, seconds - 1)), 1000);
    return () => clearInterval(timer);
  }, [otpCooldown]);

  const signingContextQuery = useQuery({
    queryKey: ["public-document-signing", publicToken, accessToken],
    queryFn: () => getPublicSigningSession({ publicToken, accessToken }),
    enabled: Boolean(accessToken),
    retry: false,
    refetchOnWindowFocus: false,
    refetchOnReconnect: false,
  });

  useEffect(() => {
    if (signingContextQuery.data) {
      const initialValues = signingContextQuery.data.fieldValues || {};
      const initialAssets = signingContextQuery.data.assetUrls || {};

      const withDateDefaults = { ...initialValues };
      (signingContextQuery.data.clientFields || []).forEach((field) => {
        if (field.fieldType === "DATE" && field.signerRole === "CLIENT" && !withDateDefaults[field.name]) {
          withDateDefaults[field.name] = todayIsoDate();
        }
      });

      setFieldValues(withDateDefaults);
      setAssetUrls(initialAssets);

      const filled = new Set();
      (signingContextQuery.data.clientFields || []).forEach((field) => {
        const hasValue =
          field.fieldType === "SIGNATURE" || field.fieldType === "PHOTO"
            ? Boolean(initialAssets[field.name] || initialValues[field.name])
            : field.fieldType === "CHECKBOX"
              ? initialValues[field.name] === "true"
              : Boolean(String(initialValues[field.name] || "").trim());
        if (hasValue) filled.add(field.name);
      });
      prefilledNamesRef.current = filled;
    }
  }, [signingContextQuery.data]);

  const allFields = useMemo(() => signingContextQuery.data?.clientFields || [], [signingContextQuery.data]);
  // Values already arrive upper-cased from the server; this keeps what the client types looking the
  // same as what the finished PDF will print.
  const uppercase = Boolean(signingContextQuery.data?.uppercaseValues);
  const isCompleted = ["COMPLETED", "CLIENT_SIGNED", "EXPIRED"].includes(infoQuery.data?.status);

  const isEditableForClient = (field) => {
    if (isCompleted) return false;
    // A field on a branch the form did not take is not the client's to fill, and its rectangle
    // must not appear over the preview.
    if (!isFieldVisible(field, fieldValues)) return false;
    if (field.signerRole === "CLIENT" || field.signerRole === "ADMIN_IF_NOT_CLIENT_FILLED") return true;
    // Both fall to the client only when nobody filled them before the link went out: the linked
    // detail for CLIENT_IF_NONE, the sending admin for CLIENT_IF_NOT_ADMIN_FILLED.
    if (field.signerRole === "CLIENT_IF_NONE" || field.signerRole === "CLIENT_IF_NOT_ADMIN_FILLED") {
      return !prefilledNamesRef.current.has(field.name);
    }
    return false;
  };

  const firstFieldByName = useMemo(() => {
    const map = new Map();
    allFields.forEach((field) => {
      if (!map.has(field.name)) map.set(field.name, field);
    });
    return map;
  }, [allFields]);

  const htmlForDisplay = useMemo(() => {
    const raw = signingContextQuery.data?.htmlContent || "";
    if (!raw) return "";
    const today = new Date();
    const todayFormatted = formatDisplayDate(today).replace(/ /g, "-");
    const orgLogoUrl = signingContextQuery.data?.organizationLogoUrl;
    const orgName = signingContextQuery.data?.organizationName || "";

    const wrapFill = (html) => (html ? `<span class="ka-fill">${html}</span>` : `<span class="ka-fill">&nbsp;</span>`);
    const mirrorHtml = (name) => {
      // Same rule the page overlay follows: a field whose condition does not hold shows nothing,
      // whatever value it still carries from the branch the form did not take.
      const mirrored = firstFieldByName.get(name);
      if (mirrored && !isFieldVisible(mirrored, fieldValues)) return wrapFill("");
      const image = assetUrls[name] || (typeof fieldValues[name] === "string" && fieldValues[name].startsWith("data:") ? fieldValues[name] : null);
      if (image) return wrapFill(`<img src="${image}" style="max-height:60px;max-width:260px;display:block;" />`);
      const text = fieldValues[name];
      return wrapFill(text ? escapeHtml(String(text)) : "");
    };

    const seenFieldNames = new Set();
    const withTokens = raw
      .replace(/\[\[FIELD:([a-zA-Z0-9_]+)]]/g, (_, name) => {
        if (seenFieldNames.has(name)) {
          return mirrorHtml(name);
        }
        seenFieldNames.add(name);
        return `<span class="ka-fill" data-field-slot="${name}"></span>`;
      })
      .replace(/\[\[ORG_LOGO]]/g, orgLogoUrl ? `<img src="${orgLogoUrl}" style="max-height:48px;max-width:160px;" />` : "")
      .replace(/\[\[ORG_NAME]]/g, wrapFill(escapeHtml(orgName)))
      .replace(/\[\[SIGNED_DATE]]/g, wrapFill(todayFormatted));

    const parsed = new DOMParser().parseFromString(withTokens, "text/html");
    const pageEls = Array.from(parsed.querySelectorAll(".pdf-page"));
    if (pageEls.length === 0) return `<div class="ka-doc">${withTokens}</div>`;

    const styleEl = parsed.querySelector("style");
    const headerEl = parsed.querySelector(".page-header-repeat");
    const footerEl = parsed.querySelector(".global-footer");
    const pageCount = pageEls.length;
    const headerHtml = headerEl ? headerEl.outerHTML : "";

    const footerHtmlFor = (interactive) => {
      if (!footerEl) return "";
      const clone = footerEl.cloneNode(true);
      clone.querySelectorAll("[data-field-slot]").forEach((span) => {
        const name = span.getAttribute("data-field-slot");
        if (interactive && isEditableForClient(firstFieldByName.get(name))) return;
        span.removeAttribute("data-field-slot");
        span.innerHTML = mirrorHtml(name);
      });
      return clone.outerHTML;
    };

    const pagesHtml = pageEls
      .map(
        (el, i) =>
          `<div class="signing-page-sheet" style="background:#fff;border:1px solid #e0e0e0;box-shadow:0 1px 4px rgba(0,0,0,0.08);padding:24px;margin-bottom:20px;border-radius:4px;">` +
          headerHtml +
          el.innerHTML +
          footerHtmlFor(i === pageCount - 1) +
          `<div style="text-align:center;color:#999;font-size:11px;margin-top:16px;">Page ${i + 1} of ${pageCount}</div>` +
          `</div>`
      )
      .join("");

    return (
      `<div class="ka-doc">` +
      (styleEl ? styleEl.outerHTML : "") +
      `<style>${SIGNING_DISPLAY_STYLE}</style>` +
      pagesHtml +
      `</div>`
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [signingContextQuery.data?.htmlContent, signingContextQuery.data?.organizationLogoUrl, assetUrls, fieldValues, firstFieldByName]);

  useEffect(() => {
    if (!htmlContainerRef.current) return;
    const nodes = htmlContainerRef.current.querySelectorAll("[data-field-slot]");
    const map = {};
    nodes.forEach((node) => {
      const name = node.getAttribute("data-field-slot");
      map[name] = node;
      fieldRefs.current[name] = node;
    });
    setPortalNodes(map);
  }, [htmlForDisplay]);

  const sendOtpMutation = useMutation({
    mutationFn: () => sendPublicDocumentOtp(publicToken),
    onSuccess: (data) => {
      setOtpSentOnce(true);
      setOtpCooldown(data?.retryAfterSeconds ?? OTP_RESEND_SECONDS);
      toast.success(`Verification code sent to ${infoQuery.data?.clientEmail || "your email"}`);
    },
    onError: (error) => {
      if (error?.response?.status === 429) {
        // Another tab (or a stale countdown) got there first, so take the server's remaining time.
        infoQuery.refetch();
        toast.info("A code was just sent. Please wait before requesting another.");
        return;
      }
      toast.error("Failed to send verification code");
    },
  });

  const verifyOtpMutation = useMutation({
    mutationFn: () => verifyPublicDocumentOtp({ publicToken, code: otpCode }),
    onSuccess: (data) => {
      sessionStorage.setItem(sessionStorageKey(publicToken), data.accessToken);
      setAccessToken(data.accessToken);
      toast.success("Email verified");
    },
    onError: () => toast.error("Invalid or expired verification code"),
  });

  const completeMutation = useMutation({
    mutationFn: () =>
      completePublicSigning({
        publicToken,
        accessToken,
        signatureDataUrl: fieldValues,
      }),
    onSuccess: () => {
      toast.success("Document submitted");
      setConfirmSubmitOpen(false);
      infoQuery.refetch();
    },
    onError: () => toast.error("Failed to submit document"),
  });

  const readyToSign = infoQuery.data?.status === "SENT" && signingContextQuery.data;
  const showSignedDocument = ["COMPLETED", "CLIENT_SIGNED"].includes(infoQuery.data?.status) && Boolean(signingContextQuery.data);

  // A phone cannot spare the height the whole action card takes, so it scrolls away and only the
  // submit button comes back, pinned. boundingClientRect.top separates "scrolled past" from "not
  // reached yet", which both report isIntersecting false.
  useEffect(() => {
    const sentinel = submitSentinelRef.current;
    if (!sentinel) return undefined;
    const observer = new IntersectionObserver(([entry]) =>
      setSubmitBarPinned(!entry.isIntersecting && entry.boundingClientRect.top < 0)
    );
    observer.observe(sentinel);
    return () => observer.disconnect();
  }, [readyToSign]);

  const handlePointerDown = (event) => {
    const canvas = canvasRef.current;
    const point = canvasPoint(canvas, event);
    if (!point) return;
    const context = canvas.getContext("2d");
    context.strokeStyle = "#0f172a";
    context.lineWidth = 2.5;
    context.lineCap = "round";
    context.beginPath();
    context.moveTo(point.x, point.y);
    isDrawingRef.current = true;
    canvas.setPointerCapture?.(event.pointerId);
  };

  const handlePointerMove = (event) => {
    if (!isDrawingRef.current) return;
    const canvas = canvasRef.current;
    const point = canvasPoint(canvas, event);
    if (!point) return;
    const context = canvas.getContext("2d");
    context.lineTo(point.x, point.y);
    context.stroke();
  };

  const clearCanvas = () => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const context = canvas.getContext("2d");
    context.clearRect(0, 0, canvas.width, canvas.height);
  };

  const saveSignature = () => {
    const dataUrl = canvasRef.current.toDataURL("image/png");
    setFieldValues((current) => ({ ...current, [activeSignatureField]: dataUrl }));
    setAssetUrls((current) => ({ ...current, [activeSignatureField]: dataUrl }));
    setSignatureDialogOpen(false);
  };

  const handlePhotoChange = async (fieldName, file) => {
    if (!file) return;
    const dataUrl = await readFileAsDataUrl(file);
    setFieldValues((current) => ({ ...current, [fieldName]: dataUrl }));
    setAssetUrls((current) => ({ ...current, [fieldName]: dataUrl }));
  };

  const renderFieldControl = (field, { compact }) => {
    const editable = isEditableForClient(field);
    const assetUrl = assetUrls[field.name] || (typeof fieldValues[field.name] === "string" && fieldValues[field.name].startsWith("data:") ? fieldValues[field.name] : null);
    const inputStyle = {
      ...(compact
        ? { border: "1px solid #94a3b8", borderRadius: 4, font: "inherit", fontSize: 13, padding: "4px 6px", outline: "none", minWidth: 0, width: 180, maxWidth: "100%" }
        : { width: "100%", height: "100%", border: "none", background: "transparent", font: "inherit", fontSize: 12, padding: 0, outline: "none", resize: "none" }),
      ...(uppercase ? { textTransform: "uppercase" } : {}),
    };
    const boxStyle = compact
      ? { position: "relative", display: "inline-block", verticalAlign: "middle", border: editable ? "2px dashed" : "1px solid", borderColor: editable ? "warning.main" : "rgba(15,23,42,0.16)", borderRadius: 1, overflow: "hidden", cursor: editable && field.fieldType === "SIGNATURE" ? "pointer" : "default" }
      : { width: "100%", height: "100%", cursor: editable && field.fieldType === "SIGNATURE" ? "pointer" : "default" };

    if (field.fieldType === "PHOTO") {
      return (
        <Box sx={{ ...boxStyle, width: compact ? 200 : "100%", maxWidth: "100%", height: compact ? 140 : "100%" }}>
          {assetUrl && <img src={assetUrl} alt={field.label} style={{ width: "100%", height: "100%", objectFit: "contain" }} />}
          {editable && (
            <input
              type="file"
              accept="image/*"
              onChange={(event) => handlePhotoChange(field.name, event.target.files?.[0])}
              style={{ position: "absolute", inset: 0, opacity: 0, cursor: "pointer" }}
            />
          )}
          {editable && !assetUrl && (
            <Box sx={{ display: "flex", alignItems: "center", justifyContent: "center", height: "100%", fontSize: 11 }}>Upload photo</Box>
          )}
        </Box>
      );
    }
    if (field.fieldType === "SIGNATURE") {
      return (
        <Box
          onClick={() => editable && (setActiveSignatureField(field.name), setSignatureDialogOpen(true), requestAnimationFrame(() => clearCanvas()))}
          sx={{ ...boxStyle, width: compact ? 240 : "100%", maxWidth: "100%", height: compact ? 70 : "100%" }}
        >
          {assetUrl ? (
            <img src={assetUrl} alt={field.label} style={{ width: "100%", height: "100%", objectFit: "contain" }} />
          ) : (
            editable && <Box sx={{ display: "flex", alignItems: "center", justifyContent: "center", height: "100%", fontSize: 11 }}>Sign here</Box>
          )}
        </Box>
      );
    }
    if (!editable) {
      // A ticked checkbox reads as a checkmark, never the literal "true"; an empty one shows
      // nothing. Anything else (an admin-filled detail on a branch the client cannot change) is
      // plain read-only text.
      if (field.fieldType === "CHECKBOX") {
        const checked = fieldValues[field.name] === "true";
        return (
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              height: compact ? "auto" : "100%",
              fontSize: compact ? 15 : 14,
              fontWeight: 700,
              lineHeight: 1,
            }}
          >
            {checked ? "✓" : ""}
          </Box>
        );
      }
      // A comb value (a MARN/LPN) reads one character per cell, matching the form's boxed layout,
      // instead of the raw string bunched together.
      if (field.combCells) {
        const chars = fieldValues[field.name] || "";
        return (
          <Box sx={{ display: compact ? "inline-flex" : "flex", width: compact ? "auto" : "100%", height: compact ? 22 : "100%" }}>
            {Array.from({ length: field.combCells }).map((_, cellIndex) => (
              <Box
                key={cellIndex}
                sx={{
                  flex: compact ? "none" : 1,
                  width: compact ? 16 : undefined,
                  textAlign: "center",
                  fontSize: 12,
                  lineHeight: compact ? "22px" : undefined,
                  borderRight: cellIndex < field.combCells - 1 ? "1px solid rgba(15,23,42,0.25)" : "none",
                }}
              >
                {(chars[cellIndex] || "").trim()}
              </Box>
            ))}
          </Box>
        );
      }
      return compact
        ? <span>{fieldValues[field.name] || "-"}</span>
        : <Box sx={{ whiteSpace: "pre-wrap", lineHeight: 1.15 }}>{fieldValues[field.name] || ""}</Box>;
    }
    if (field.fieldType === "MULTILINE") {
      return (
        <textarea
          value={fieldValues[field.name] || ""}
          onChange={(event) => setFieldValues((current) => ({ ...current, [field.name]: event.target.value }))}
          style={compact ? { ...inputStyle, display: "block", width: "100%", minHeight: 60 } : inputStyle}
        />
      );
    }
    if (field.fieldType === "CHECKBOX") {
      return (
        <input
          type="checkbox"
          checked={fieldValues[field.name] === "true"}
          onChange={(event) =>
            setFieldValues((current) => applyCheckboxChange(allFields, current, field, event.target.checked))
          }
          style={compact ? { width: 18, height: 18, verticalAlign: "middle", cursor: "pointer" } : { width: "100%", height: "100%", margin: 0, cursor: "pointer" }}
        />
      );
    }
    if (field.combCells) {
      return (
        <Box sx={{ display: compact ? "inline-flex" : "flex", width: compact ? "auto" : "100%", height: compact ? 32 : "100%" }}>
          {Array.from({ length: field.combCells }).map((_, cellIndex) => {
            const raw = (fieldValues[field.name] || "")[cellIndex] || "";
            return (
              <input
                key={cellIndex}
                ref={(el) => {
                  combRefs.current[`${field.name}-${cellIndex}`] = el;
                }}
                type="text"
                inputMode="text"
                maxLength={1}
                value={raw.trim() ? raw : ""}
                onChange={(event) => {
                  const char = event.target.value.slice(-1);
                  setCombChar(field, cellIndex, char);
                  if (char && cellIndex < field.combCells - 1) {
                    combRefs.current[`${field.name}-${cellIndex + 1}`]?.focus();
                  }
                }}
                onKeyDown={(event) => {
                  if (event.key === "Backspace" && !raw.trim() && cellIndex > 0) {
                    setCombChar(field, cellIndex - 1, "");
                    combRefs.current[`${field.name}-${cellIndex - 1}`]?.focus();
                  }
                }}
                style={{
                  ...inputStyle,
                  flex: compact ? "none" : 1,
                  width: compact ? 26 : undefined,
                  minWidth: 0,
                  textAlign: "center",
                  borderRight: cellIndex < field.combCells - 1 ? "1px solid rgba(15,23,42,0.3)" : "none",
                }}
              />
            );
          })}
        </Box>
      );
    }
    return (
      <input
        type={field.fieldType === "DATE" ? "date" : "text"}
        value={fieldValues[field.name] || ""}
        onChange={(event) => setFieldValues((current) => ({ ...current, [field.name]: event.target.value }))}
        style={inputStyle}
      />
    );
  };

  const pagePreviewFields = useMemo(
    () => visibleFieldsByPage(allFields, fieldValues),
    [allFields, fieldValues]
  );

  const isFieldFilled = (field) => {
    if (field.fieldType === "SIGNATURE" || field.fieldType === "PHOTO") {
      const value = assetUrls[field.name] || fieldValues[field.name];
      return Boolean(value && (typeof value !== "string" || value.length > 0));
    }
    if (field.fieldType === "CHECKBOX") {
      return fieldValues[field.name] === "true";
    }
    return Boolean(String(fieldValues[field.name] || "").trim());
  };

  const clientFieldChecklist = useMemo(() => {
    const seen = new Set();
    const list = [];
    for (const field of allFields) {
      if (!isEditableForClient(field) || seen.has(field.name)) continue;
      seen.add(field.name);

      // ADMIN_IF_NOT_CLIENT_FILLED never blocks (admin closes the gap post-sign); a
      // CLIENT_IF_NOT_ADMIN_FILLED field reaching the client always does (admin already passed).
      const required =
        field.signerRole === "ADMIN_IF_NOT_CLIENT_FILLED"
          ? false
          : field.signerRole === "CLIENT_IF_NOT_ADMIN_FILLED" || field.required;
      list.push({ name: field.name, label: field.label, required, filled: isFieldFilled(field) });
    }
    return list;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [allFields, fieldValues, assetUrls]);

  const missingRequiredLabels = clientFieldChecklist.filter((f) => f.required && !f.filled).map((f) => f.label);

  const scrollToField = (name) => {
    const el = fieldRefs.current[name];
    if (!el) return;
    el.scrollIntoView({ behavior: "smooth", block: "center" });
    const input = el.querySelector("input, textarea");
    if (input) {
      input.focus();
    } else {
      el.style.outline = "3px solid #2563eb";
      el.style.outlineOffset = "2px";
      setTimeout(() => {
        el.style.outline = "";
        el.style.outlineOffset = "";
      }, 1200);
    }
  };

  const handleSubmitClick = () => {
    if (missingRequiredLabels.length) {
      toast.error(`Please fill in: ${missingRequiredLabels.join(", ")}`);
      return;
    }
    setConfirmSubmitOpen(true);
  };

  return (
    <>
      <Backdrop open={infoQuery.isLoading || signingContextQuery.isFetching} sx={{ color: "#fff", zIndex: 1300 }}>
        <CircularProgress color="inherit" />
      </Backdrop>

      <div className="min-h-screen bg-slate-100 py-10 px-4">
        <Box sx={{ maxWidth: 1180, mx: "auto" }}>
          <Stack spacing={3}>
            <Card sx={{ borderRadius: 5 }}>
              <CardContent sx={{ p: 4 }}>
                {infoQuery.isError ? (
                  <>
                    <Typography variant="h4" fontWeight={800}>
                      Document signing
                    </Typography>
                    <Alert severity="error" sx={{ mt: 3 }}>
                      {signingLinkErrorMessage(infoQuery.error)}
                    </Alert>
                  </>
                ) : (
                  <>
                    <Typography variant="h4" fontWeight={800}>
                      {infoQuery.data?.title || "Document signing"}
                    </Typography>
                    <Typography color="text.secondary" sx={{ mt: 1 }}>
                      {infoQuery.data?.organizationName} prepared this document for {infoQuery.data?.clientName}.
                    </Typography>
                    {isCompleted && (
                      <Alert severity={infoQuery.data?.status === "EXPIRED" ? "error" : "success"} sx={{ mt: 3 }}>
                        {infoQuery.data?.status === "EXPIRED"
                          ? "This signing link has expired."
                          : "This document is no longer awaiting your action."}
                      </Alert>
                    )}

                    {!accessToken && !isCompleted && (
                      <Stack spacing={2} sx={{ mt: 3, maxWidth: 440 }}>
                        <Alert severity={otpSentOnce ? "success" : "info"}>
                          {otpSentOnce
                            ? `We sent a one-time code to ${infoQuery.data?.clientEmail}. Enter it below. It stays valid for 10 minutes.`
                            : `Verify your email first. A one-time code will be sent to ${infoQuery.data?.clientEmail}.`}
                        </Alert>
                        <Box>
                          <Button
                            variant="contained"
                            fullWidth
                            onClick={() => sendOtpMutation.mutate()}
                            disabled={otpCooldown > 0 || sendOtpMutation.isLoading}
                          >
                            {otpCooldown > 0
                              ? `Resend in ${formatCountdown(otpCooldown)}`
                              : otpSentOnce
                                ? "Resend verification code"
                                : "Send verification code"}
                          </Button>
                          {otpCooldown > 0 && (
                            <Typography variant="caption" color="text.secondary" sx={{ mt: 0.75, display: "block" }}>
                              Didn't get it? Check your spam folder. You can request another code once
                              the timer runs out.
                            </Typography>
                          )}
                        </Box>
                        <TextField label="Verification code" value={otpCode} onChange={(event) => setOtpCode(event.target.value)} fullWidth />
                        <Button
                          variant="outlined"
                          onClick={() => verifyOtpMutation.mutate()}
                          disabled={!otpCode || verifyOtpMutation.isLoading}
                        >
                          Verify and open document
                        </Button>
                      </Stack>
                    )}
                  </>
                )}
              </CardContent>
            </Card>

            {readyToSign && (
              <Card sx={{ borderRadius: 5, position: { xs: "static", md: "sticky" }, top: { md: 12 }, zIndex: 10 }}>
                <CardContent sx={{ p: 2.5 }}>
                  <Stack direction={{ xs: "column", md: "row" }} justifyContent="space-between" alignItems={{ md: "center" }} spacing={2}>
                    <Alert severity="info" sx={{ flex: 1 }}>
                      Fill in the highlighted fields directly on the document below, then submit.
                    </Alert>
                    <Button variant="contained" color="success" disabled={completeMutation.isLoading} onClick={handleSubmitClick}>
                      Submit Document
                    </Button>
                  </Stack>
                  {clientFieldChecklist.length > 0 && (
                    <Stack direction="row" flexWrap="wrap" gap={1} sx={{ mt: 2 }}>
                      {clientFieldChecklist.map((field) => (
                        <Chip
                          key={field.name}
                          size="small"
                          clickable
                          onClick={() => scrollToField(field.name)}
                          color={field.filled ? "success" : "error"}
                          variant={field.filled ? "filled" : "outlined"}
                          label={field.required ? `${field.label} *` : field.label}
                        />
                      ))}
                    </Stack>
                  )}
                </CardContent>
              </Card>
            )}

            {/* Zero height, and it gives up the Stack gap it would otherwise add. */}
            {readyToSign && <Box ref={submitSentinelRef} sx={{ height: 0, marginTop: "0 !important" }} />}

            {(readyToSign || showSignedDocument) && (
              <>
                {showSignedDocument && !readyToSign && (
                  <Alert severity="success" sx={{ borderRadius: 3 }}>
                    You already submitted this document. It is shown below, read-only.
                  </Alert>
                )}

                {signingContextQuery.data.contentType === "GENERATED_HTML" ? (
                  <Card sx={{ borderRadius: 5 }}>
                    <CardContent sx={{ p: { xs: 2, md: 4 } }}>
                      <Box ref={htmlContainerRef} dangerouslySetInnerHTML={{ __html: htmlForDisplay }} />
                      {/* One portal per distinct name, not per field row. Several rows can share a
                          name (mirrored elsewhere in the document), but only one live DOM slot for
                          that name ever survives htmlForDisplay's mirroring pass. */}
                      {Array.from(firstFieldByName.values()).map((field) =>
                        portalNodes[field.name] && isFieldVisible(field, fieldValues)
                          ? createPortal(renderFieldControl(field, { compact: true }), portalNodes[field.name])
                          : null
                      )}
                    </CardContent>
                  </Card>
                ) : (
                  signingContextQuery.data.previewImageUrls.map((imageUrl, pageIndex) => (
                    <Card key={imageUrl} sx={{ borderRadius: 5, overflow: "hidden" }}>
                      <Box sx={{ position: "relative", backgroundColor: "#fff" }}>
                        <img src={imageUrl} alt={`Document page ${pageIndex + 1}`} style={{ width: "100%", display: "block" }} />
                        {(pagePreviewFields.get(pageIndex + 1) || []).map((field) => {
                          const editable = isEditableForClient(field);
                          return (
                            <Box
                              key={field.id}
                              ref={(el) => {
                                if (el && !fieldRefs.current[field.name]) fieldRefs.current[field.name] = el;
                              }}
                              sx={{
                                position: "absolute",
                                left: `${field.xPct * 100}%`,
                                top: `${field.yPct * 100}%`,
                                width: `${field.widthPct * 100}%`,
                                height: `${field.heightPct * 100}%`,
                                border: editable ? "2px dashed" : "1px solid",
                                borderColor: editable ? "warning.main" : "rgba(15,23,42,0.16)",
                                backgroundColor: editable ? "rgba(245,158,11,0.12)" : "rgba(255,255,255,0.08)",
                                overflow: "hidden",
                                p: 0.25,
                                fontSize: 11,
                                fontWeight: 600,
                                color: "#0f172a",
                              }}
                            >
                              {renderFieldControl(field, { compact: false })}
                            </Box>
                          );
                        })}
                      </Box>
                    </Card>
                  ))
                )}
              </>
            )}
          </Stack>
        </Box>
      </div>

      {readyToSign && (
        <Box
          sx={{
            display: { xs: submitBarPinned ? "block" : "none", md: "none" },
            position: "fixed",
            top: 0,
            left: 0,
            right: 0,
            zIndex: 1200,
            px: 2,
            py: 1.5,
            backgroundColor: "background.paper",
            boxShadow: 3,
          }}
        >
          <Button
            fullWidth
            variant="contained"
            color="success"
            disabled={completeMutation.isLoading}
            onClick={handleSubmitClick}
          >
            Submit Document
          </Button>
        </Box>
      )}

      <Dialog open={signatureDialogOpen} onClose={() => setSignatureDialogOpen(false)} maxWidth="md" fullWidth>
        <DialogTitle>Draw your signature</DialogTitle>
        <DialogContent>
          <Box sx={{ border: "1px solid #cbd5e1", borderRadius: 3, overflow: "hidden", backgroundColor: "#fff" }}>
            <canvas
              ref={canvasRef}
              width={900}
              height={300}
              style={{ width: "100%", display: "block", touchAction: "none" }}
              onPointerDown={handlePointerDown}
              onPointerMove={handlePointerMove}
              onPointerUp={() => {
                isDrawingRef.current = false;
              }}
              onPointerLeave={() => {
                isDrawingRef.current = false;
              }}
            />
          </Box>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 3 }}>
          <Button onClick={clearCanvas}>Clear</Button>
          <Button variant="contained" onClick={saveSignature}>
            Use signature
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog open={confirmSubmitOpen} onClose={() => setConfirmSubmitOpen(false)} maxWidth="xs" fullWidth>
        <DialogTitle>Confirm submission</DialogTitle>
        <DialogContent>
          <Typography sx={{ mb: 1.5 }}>
            Please confirm carefully. Once submitted, you will not be able to edit or resubmit
            this document yourself.
          </Typography>
          <Typography color="text.secondary">
            By submitting, you agree to the terms stated in this document, if any.
          </Typography>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 3 }}>
          <Button onClick={() => setConfirmSubmitOpen(false)} disabled={completeMutation.isLoading}>
            Go back
          </Button>
          <Button variant="contained" color="success" disabled={completeMutation.isLoading} onClick={() => completeMutation.mutate()}>
            {completeMutation.isLoading ? "Submitting…" : "Confirm and submit"}
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
}

function todayIsoDate() {
  const now = new Date();
  const month = String(now.getMonth() + 1).padStart(2, "0");
  const day = String(now.getDate()).padStart(2, "0");
  return `${now.getFullYear()}-${month}-${day}`;
}

function escapeHtml(value) {
  return value.replace(/&/g, "&amp;").replace(/</g, "&lt;").replace(/>/g, "&gt;").replace(/"/g, "&quot;");
}

function readFileAsDataUrl(file) {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(reader.result);
    reader.onerror = reject;
    reader.readAsDataURL(file);
  });
}
